import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CalendarPlus, Flame } from "lucide-react";
import { api, setPageMeta } from "@/lib/api";
import { ClubRow } from "@/components/ClubRow";
import { Reveal, MaskedLines } from "@/components/Motion";
import { Ticker } from "@/components/Ticker";
import { JoinForm } from "@/components/JoinForm";

const RsvpForm = ({ event }) => {
  const [form, setForm] = useState({ name: "", email: "", distance: "", website: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/rsvp", { event_id: event.id, ...form });
      toast.success(res.data.message);
      setDone(true);
    } catch {
      toast.error("Could not RSVP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <p data-testid="rsvp-success" className="font-display text-2xl mt-6">SEE YOU OUT THERE.</p>;

  const input = "w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200";
  return (
    <form onSubmit={submit} data-testid="dallas-rsvp-form" className="mt-8 space-y-4 max-w-md">
      <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} aria-hidden="true" />
      <input data-testid="rsvp-name-input" required placeholder="NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
      <input data-testid="rsvp-email-input" required type="email" placeholder="EMAIL" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
      <div className="flex flex-wrap gap-2">
        {(event.distances || []).map((d) => (
          <button key={d} type="button" data-testid={`rsvp-distance-${d.replace(/\s/g, "-")}`} onClick={() => setForm({ ...form, distance: d })}
            className={`font-mono-label px-3 py-2 border transition-colors duration-200 ${form.distance === d ? "bg-white text-ink border-white" : "border-white/30 hover:border-white"}`}>
            {d}
          </button>
        ))}
      </div>
      <button type="submit" disabled={loading} data-testid="rsvp-submit-button" className="btn-invert bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
        {loading ? "SENDING..." : "RSVP"}
      </button>
    </form>
  );
};

const MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

export default function DallasHub() {
  const [events, setEvents] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [city, setCity] = useState(null);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    setPageMeta("Dallas Running Events | Weekly Group Runs and Races | Ninja Run Club", "The Ninja Run Club home chapter. Weekly Dallas runs, the local club directory, routes, and heat training guidance.");
    api.get("/events", { params: { city: "dallas" } }).then((r) => setEvents(r.data)).catch(() => {});
    api.get("/cities/texas/dallas").then((r) => { setCity(r.data.city); setClubs(r.data.clubs); }).catch(() => {});
    api.get("/articles", { params: { category: "Dallas" } }).then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  const next = events[0];

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden bg-ink border-b border-white/10">
        <video
          poster="/video/run-02-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          aria-label="Runners on a Dallas track at golden hour"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="dallas-hero-video"
        >
          <source src={MOBILE ? "/video/run-02-mobile.mp4" : "/video/run-02.mp4"} type="video/mp4" />
          <source src="/video/run-02.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="relative px-5 lg:px-10 pt-24 pb-20">
          <h1 className="font-mono-label text-white/60">RUN DALLAS: WEEKLY GROUP RUNS, CLUBS & ROUTES</h1>
          <MaskedLines
            lines={["DALLAS", "RUNS HERE."]}
            lineClassName="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            className="mt-5"
          />
          <p className="text-white/60 max-w-xl mt-6 text-sm lg:text-base">
            Where Ninja Run Club was born. Weekly community runs, track mornings, HYROX sessions, and a city full of crews worth joining.
          </p>
        </div>
      </header>

      <Ticker items={["SATURDAY COMMUNITY RUN", "3 MI + 5 MI OPTIONS", "WALKERS WELCOME", "PACE LEADERS", "POST-RUN COFFEE", "KATY TRAIL", "WHITE ROCK LAKE"]} />

      {next && (
        <section id="next-run" data-testid="dallas-next-run" className="px-5 lg:px-10 py-20 border-b border-white/10 bg-ink">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <Reveal>
                <p className="font-mono-label text-white/40">NEXT RUN</p>
                <h2 className="font-display text-3xl lg:text-5xl mt-4 leading-none">{next.title}</h2>
                <div className="mt-8 space-y-3 border-l border-white/20 pl-6">
                  <p className="font-mono-label text-white/80">
                    {new Date(next.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }).toUpperCase()} · {next.time} {next.timezone}
                  </p>
                  <p className="text-white/60 text-sm flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /> {next.location}</p>
                  <p className="text-white/60 text-sm">{(next.distances || []).join(" · ")}</p>
                  <p className="text-white/60 text-sm">{next.pace}</p>
                </div>
                <p className="text-white/50 text-sm mt-6 max-w-md">{next.description}</p>
                <a
                  href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(next.title + " - Ninja Run Club")}&dates=${next.date.replace(/-/g, "")}/${next.date.replace(/-/g, "")}&details=${encodeURIComponent(next.description)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="dallas-calendar-link"
                  className="mt-5 inline-flex items-center gap-2 font-mono-label text-white/50 hover:text-white transition-colors duration-200"
                >
                  <CalendarPlus size={14} /> ADD TO CALENDAR
                </a>
              </Reveal>
            </div>
            <div>
              <Reveal delay={0.15}>
                <p className="font-mono-label text-white/40">CLAIM YOUR SPOT</p>
                <RsvpForm event={next} />
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <section data-testid="dallas-schedule" className="px-5 lg:px-10 py-20 border-b border-white/10">
        <Reveal>
          <p className="font-mono-label text-white/40">THE WEEK</p>
          <h2 className="font-display text-3xl lg:text-5xl mt-4">RECURRING RUNS.</h2>
          <p className="text-white/40 text-sm mt-3 max-w-lg">Meeting points are announced to the club list each week. Join the list and you will never miss one.</p>
        </Reveal>
        <div className="mt-10">
          {events.map((e, i) => (
            <div key={e.id} className="grid grid-cols-12 gap-4 items-baseline border-t border-white/10 py-6 last:border-b" data-testid={`dallas-event-${e.id}`}>
              <span className="col-span-1 font-mono-label text-white/30">{String(i + 1).padStart(2, "0")}</span>
              <div className="col-span-11 md:col-span-4">
                <p className="font-display text-xl lg:text-2xl">{e.title}</p>
                <p className="font-mono-label text-white/40 mt-1">{e.type}</p>
              </div>
              <p className="col-span-11 col-start-2 md:col-span-3 font-mono-label text-white/60">
                {new Date(e.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase()} · {e.time}
              </p>
              <p className="col-span-11 col-start-2 md:col-span-4 text-white/50 text-sm">{(e.distances || []).join(" · ")} — {e.pace}</p>
            </div>
          ))}
        </div>
      </section>

      {articles.length > 0 && (
        <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20" data-testid="dallas-guides">
          <Reveal>
            <p className="font-mono-label text-ink/40 flex items-center gap-2"><Flame size={13} /> LOCAL KNOWLEDGE</p>
            <h2 className="font-display text-3xl lg:text-5xl mt-4">RUN DALLAS SMARTER.</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10 mt-10">
            {articles.map((a) => (
              <Link key={a.slug} to={`/training/${a.slug}`} data-testid={`dallas-article-${a.slug}`} className="group bg-paper p-8 lg:p-10 hover:bg-ink hover:text-paper transition-colors duration-300">
                <p className="font-mono-label opacity-50">{a.category} · {a.read_time} MIN</p>
                <h3 className="font-display text-2xl lg:text-3xl mt-4">{a.title}</h3>
                <p className="text-sm opacity-60 mt-3">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="px-5 lg:px-10 py-20 border-b border-white/10" data-testid="dallas-clubs">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-mono-label text-white/40">BEYOND NRC</p>
            <h2 className="font-display text-3xl lg:text-5xl mt-3">EVERY DFW CLUB.</h2>
          </div>
          <Link to="/run-clubs/texas/dallas" className="font-mono-label link-underline text-white/50 shrink-0">FULL CITY PAGE</Link>
        </div>
        {clubs.slice(0, 8).map((c, i) => (
          <ClubRow key={c.slug} club={c} index={i} />
        ))}
      </section>

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-24" data-testid="dallas-join">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-display text-4xl lg:text-5xl leading-none">GET THE WEEKLY DETAILS.</h2>
            <p className="text-ink/60 text-sm mt-5 max-w-md">Meeting points, route maps, pace groups, and partner activations land in your inbox every week. Dallas is the home chapter. Act like it.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <JoinForm source="dallas-hub" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
